// Mapping of task names/keywords to vital types
const VITAL_TASK_KEYWORDS = {
    // Blood Pressure
    "blood pressure": "blood_pressure",
    "bp measurement": "blood_pressure",
    "measure blood pressure": "blood_pressure",
    "check blood pressure": "blood_pressure",

    // Temperature
    temperature: "temperature",
    "body temperature": "temperature",
    "measure temperature": "temperature",
    "check temperature": "temperature",
    "take temperature": "temperature",

    // Pulse
    pulse: "pulse",
    "heart rate": "pulse",
    "check pulse": "pulse",
    "measure pulse": "pulse",
    "pulse rate": "pulse",

    // SpO2
    spo2: "spo2",
    "oxygen saturation": "spo2",
    "oxygen level": "spo2",
    "blood oxygen": "spo2",
    "pulse oximetry": "spo2",
    "pulse oximeter": "spo2",

    // Respiration
    respiration: "respiration",
    "respiratory rate": "respiration",
    "breathing rate": "respiration",
    "breathing assessment": "respiration",

    // Pain
    pain: "pain_level",
    "pain level": "pain_level",
    "pain assessment": "pain_level",
    "pain scale": "pain_level",

    // Weight
    weight: "weight",
    "body weight": "weight",
    "weigh patient": "weight",
    "measure weight": "weight",

    // BMI
    bmi: "bmi",
    "body mass index": "bmi",
    "calculate bmi": "bmi",
    "check bmi": "bmi",

    // Blood Glucose
    "blood glucose": "blood_glucose",
    "blood sugar": "blood_glucose",
    glucose: "blood_glucose",
    "sugar level": "blood_glucose",
    glucometer: "blood_glucose",
}

const VITAL_CATEGORIES = [
    "vital signs",
    "vitals",
    "health monitoring",
    "basic health",
    "vital signs & basic health monitoring",
]

export function getVitalType(taskName, categoryName) {
    if (!taskName) return null

    const normalizedTask = taskName.toLowerCase().trim()
    const normalizedCategory = (categoryName || "").toLowerCase().trim()

    const isVitalCategory = VITAL_CATEGORIES.some((cat) => normalizedCategory.includes(cat))

    for (const keyword in VITAL_TASK_KEYWORDS) {
        if (normalizedTask.includes(keyword)) {
            return VITAL_TASK_KEYWORDS[keyword]
        }
    }

    if (isVitalCategory) {
        if (normalizedTask.includes("pressure")) return "blood_pressure"
        if (normalizedTask.includes("temp")) return "temperature"
        if (normalizedTask.includes("heart") || normalizedTask.includes("pulse")) return "pulse"
        if (normalizedTask.includes("oxygen") || normalizedTask.includes("o2")) return "spo2"
        if (normalizedTask.includes("breath")) return "respiration"
        if (normalizedTask.includes("pain")) return "pain_level"
        if (normalizedTask.includes("bmi") || normalizedTask.includes("body mass")) return "bmi"
        if (normalizedTask.includes("weight")) return "weight"
        if (normalizedTask.includes("glucose") || normalizedTask.includes("sugar"))
            return "blood_glucose"
    }

    return null
}

export function isVitalTask(taskName, categoryName) {
    return getVitalType(taskName, categoryName) !== null
}

export const VITAL_CONFIGS = {
    blood_pressure: {
        label: "Blood Pressure",
        icon: "Heart",
        unit: "mmHg",
        color: "text-red-500",
        normalRange: {
            systolicMin: 90,
            systolicMax: 140,
            diastolicMin: 60,
            diastolicMax: 90,
        },
    },
    temperature: {
        label: "Temperature",
        icon: "Thermometer",
        unit: "°F",
        color: "text-orange-500",
        normalRange: { min: 96.0, max: 99.5 },
    },
    pulse: {
        label: "Pulse",
        icon: "Activity",
        unit: "bpm",
        color: "text-pink-500",
        normalRange: { min: 60, max: 100 },
    },
    spo2: {
        label: "SpO2",
        icon: "Wind",
        unit: "%",
        color: "text-blue-500",
        normalRange: { min: 95, max: 100 },
    },
    respiration: {
        label: "Respiration",
        icon: "Wind",
        unit: "breaths/min",
        color: "text-cyan-500",
        normalRange: { min: 12, max: 20 },
    },
    pain_level: {
        label: "Pain Level",
        icon: "AlertCircle",
        unit: "/10",
        color: "text-yellow-500",
        normalRange: { min: 0, max: 3 },
    },
    bmi: {
        label: "BMI",
        icon: "Scale",
        unit: "kg/m²",
        color: "text-amber-500",
        normalRange: { min: 18.5, max: 24.9 },
    },
    weight: {
        label: "Weight",
        icon: "Scale",
        unit: "kg",
        color: "text-green-500",
    },
    blood_glucose: {
        label: "Blood Glucose",
        icon: "Droplet",
        unit: "mg/dL",
        color: "text-purple-500",
        normalRange: { min: 70, max: 140 },
    },
}

export function isAbnormal(vitalType, readingValue) {
    const config = VITAL_CONFIGS[vitalType]
    if (!config || !config.normalRange) return false

    switch (vitalType) {
        case "blood_pressure":
            return (
                readingValue.systolic < config.normalRange.systolicMin ||
                readingValue.systolic > config.normalRange.systolicMax ||
                readingValue.diastolic < config.normalRange.diastolicMin ||
                readingValue.diastolic > config.normalRange.diastolicMax
            )

        case "temperature":
            let valueInF = readingValue.value
            if (readingValue.unit === "C") {
                valueInF = (readingValue.value * 9) / 5 + 32
            }
            return valueInF < config.normalRange.min || valueInF > config.normalRange.max

        case "pulse":
            return (
                readingValue.bpm < config.normalRange.min ||
                readingValue.bpm > config.normalRange.max
            )

        case "spo2":
            return readingValue.percentage < config.normalRange.min

        case "respiration":
            return (
                readingValue.breaths_per_minute < config.normalRange.min ||
                readingValue.breaths_per_minute > config.normalRange.max
            )

        case "pain_level":
            return readingValue.level > config.normalRange.max

        case "bmi":
            return readingValue.value < config.normalRange.min || readingValue.value > config.normalRange.max

        case "blood_glucose":
            let valueInMgDl = readingValue.value
            if (readingValue.unit === "mmol/L") {
                valueInMgDl = readingValue.value * 18
            }
            return valueInMgDl < config.normalRange.min || valueInMgDl > config.normalRange.max

        default:
            return false
    }
}

export function formatVitalReading(vitalType, readingValue) {
    switch (vitalType) {
        case "blood_pressure":
            return `${readingValue.systolic}/${readingValue.diastolic} mmHg`

        case "temperature":
            return `${readingValue.value}°${readingValue.unit}`

        case "pulse":
            return `${readingValue.bpm} bpm`

        case "spo2":
            return `${readingValue.percentage}%`

        case "respiration":
            return `${readingValue.breaths_per_minute}/min`

        case "pain_level":
            return `${readingValue.level}/10${
                readingValue.location ? ` (${readingValue.location})` : ""
            }`

        case "bmi":
            return `${readingValue.value} kg/m²${
                readingValue.category ? ` (${readingValue.category})` : ""
            }`

        case "weight":
            return `${readingValue.value} ${readingValue.unit}`

        case "blood_glucose":
            return `${readingValue.value} ${readingValue.unit}`

        default:
            return JSON.stringify(readingValue)
    }
}

/**
 * Get a numeric value from a candidate field.
 *
 * @param {unknown} value - Candidate numeric value.
 * @returns {number | null} Parsed numeric value or null.
 */
function getNumericCandidate(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value
    if (typeof value === "string") {
        const parsedValue = Number(value)
        return Number.isFinite(parsedValue) ? parsedValue : null
    }

    return null
}

/**
 * Get patient height in centimeters from a raw height_cm value.
 *
 * @param {object | null | undefined} patientData - Patient payload containing details height fields.
 * @returns {number | null} Height in centimeters when available.
 */
export function getPatientHeightCentimeters(patientData) {
    const rawHeight =
        patientData?.additional_information?.height_cm ??
        patientData?.details?.additional_information?.height_cm ??
        patientData?.details?.extraFields?.height_cm ??
        patientData?.details?.extra_fields?.height_cm ??
        patientData?.details?.height_cm ??
        patientData?.height_cm

    const heightCentimeters = getNumericCandidate(rawHeight)

    if (!Number.isFinite(heightCentimeters) || heightCentimeters <= 0) return null

    return heightCentimeters
}

/**
 * Get patient height in meters from patient details height fields.
 *
 * @param {object | null | undefined} patientData - Patient payload containing details height fields.
 * @returns {number | null} Height in meters when available.
 */
export function getPatientHeightMeters(patientData) {
    const heightCentimeters = getPatientHeightCentimeters(patientData)

    if (!Number.isFinite(heightCentimeters) || heightCentimeters <= 0) return null

    return heightCentimeters / 100
}

/**
 * Get the BMI value for a weight and height.
 *
 * @param {number} weightKg - Weight in kilograms.
 * @param {number} heightMeters - Height in meters.
 * @returns {number | null} BMI rounded to one decimal place.
 */
export function getBmiValue(weightKg, heightMeters) {
    if (!Number.isFinite(weightKg) || weightKg <= 0) return null
    if (!Number.isFinite(heightMeters) || heightMeters <= 0) return null

    return Math.round((weightKg / (heightMeters * heightMeters)) * 10) / 10
}

/**
 * Get the BMI category for a BMI value.
 *
 * @param {number} bmiValue - Calculated BMI value.
 * @returns {string | null} BMI category label.
 */
export function getBmiCategory(bmiValue) {
    if (!Number.isFinite(bmiValue) || bmiValue <= 0) return null
    if (bmiValue < 18.5) return "underweight"
    if (bmiValue < 25) return "normal"
    if (bmiValue < 30) return "overweight"
    return "obese"
}
